import React, { Suspense, useCallback, useContext, useEffect, useMemo, useState } from "react";
import styles from "./PlaceholderBundle.module.css";
import { ProductObj, VariantsObj } from "../../Types";
import { PlaceholderBundleContext, ExtendedPlaceholderStepsType, PlaceholderMap } from "./PlaceholderBundle";
const ProductInfoModal = React.lazy(() => import("./ProductInfoModal"));
import { getPlaceholderCardWidth } from "./PlaceholderConstants";
import { CustomSwatches, Swatches, VariantSelectorV2 } from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import {
    buildMetafieldFromSwatchOptions,
    getFinalPrice,
    getPixelsForImage,
    getPriceLabel,
    DISCOUNT_TYPES,
} from "../../constants";
import ProductImageWrapper from "../../BundlePreviewWrapper/ProductImageWrapper";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { getProductId, NAGAIA } from "../../../constants";

export const RenderPrice = ({
    product,
    bundleConfig,
    money_format,
    currency,
    disPrice,
    selectedVariants,
    origPrice,
    isMobile,
}: any) => {
    const getPrice = (type: any, price: any) => {
        const finalPrice = getFinalPrice(type, 0, price);
        return finalPrice <= 0 ? bundleConfig.free_text || "Free" : getPriceLabel(finalPrice, money_format, currency);
    };

    const getNewPriceLabel = () => {
        const discountedPrice = getFinalPrice("none", 0, disPrice);
        const compareAtPrice = getFinalPrice(
            "none",
            0,
            bundleConfig.use_compare_at_price
                ? selectedVariants.reduce((total: any, sv: any) => total + +sv.compareAtPrice, 0)
                : origPrice,
        );

        if (discountedPrice < compareAtPrice) {
            return getPriceLabel(compareAtPrice, money_format, currency);
        }
        if (!product.checked) {
            return null;
        }
        return null;
    };
    return (
        <div
            style={{
                width: "100%",
                color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "flex-start",
                flexWrap: "wrap",
                alignItems: "baseline",
                gap: 3,
                position: "relative",
            }}
            className={styles.renderPriceWrapper}
        >
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                <div className={styles.disProductPrice}>
                    {bundleConfig?.discount_type === "percentage"
                        ? getPrice("none", disPrice)
                        : getPrice("none", disPrice)}
                </div>
            </InspectorEditableBlock>
            {(bundleConfig.apply_discount_on_compare_price &&
                (bundleConfig.discount_type === "fixed" || bundleConfig.discount_type === "flat")) ||
            bundleConfig.discount_type === "percentage" ? (
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCompareAtPrice">
                    <div
                        style={{
                            textDecoration: "line-through",
                            fontSize: bundleConfig?.bundles_design.product_price_size,
                            color: bundleConfig?.bundles_design.product_price_color,
                            fontWeight: bundleConfig?.bundles_design?.product_price_style?.split(" ")[0],
                            fontStyle: bundleConfig?.bundles_design?.product_price_style?.split(" ")[1],
                            width: "max-content",
                            lineHeight: "normal",
                        }}
                        className={styles.strikeOffProductPrice}
                    >
                        {getNewPriceLabel()}
                    </div>
                </InspectorEditableBlock>
            ) : (
                ""
            )}
        </div>
    );
};

export const RenderVariantInfo = ({ product }: { product: ProductObj }) => {
    const { isMobile, bundleConfig } = useContext(PlaceholderBundleContext);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                justifyContent: "flex-start",
                alignItems: "flex-start",
                fontSize: bundleConfig?.bundles_design.product_name_size - 2 + "px",
                fontWeight: +bundleConfig?.bundles_design?.product_name_style?.split(" ")[0] - 100,
            }}
        >
            {(product.options?.length || 0) > 1 ? (
                product.options?.map((option: any, idx: number) => {
                    return (
                        <div
                            style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                alignItems: isMobile ? "center" : "flex-start",
                                justifyContent: isMobile ? "space-between" : "flex-start",
                                gap: isMobile ? 4 : 8,
                                lineHeight: "normal",
                            }}
                            title={`${product.title}: ${product?.title?.split("/")[idx]}`}
                        >
                            {option.name}: {product?.title?.split("/")[idx]}
                        </div>
                    );
                })
            ) : product?.options?.[0]?.name?.toLowerCase() != "title" ? (
                <div
                    style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        alignItems: isMobile ? "center" : "flex-start",
                        justifyContent: isMobile ? "space-between" : "flex-start",
                        gap: isMobile ? 4 : 8,
                        lineHeight: "normal",
                    }}
                    title={`${product?.options?.[0]?.name}: ${product?.title?.split("/")[0]}`}
                >
                    {product?.options?.[0]?.name}: {product?.title?.split("/")[0]}
                </div>
            ) : (
                <></>
            )}
        </div>
    );
};

const ProductCard = React.memo(
    ({
        product,
        index,
        setTrackVariant = () => {},
        handleAddProduct = () => {},
        isFreeGift = false,
        disableCtas = false,
        currentStep,
        activeSubStep,
        isAccorianTemplate = false,
        stepIndex,
    }: {
        product: ProductObj;
        index: number;
        setTrackVariant: any;
        handleAddProduct: any;
        isFreeGift?: boolean;
        disableCtas?: boolean;
        currentStep?: ExtendedPlaceholderStepsType;
        placeholder?: boolean;
        activeSubStep?: number;
        isAccorianTemplate?: boolean;
        stepIndex?: number;
    }) => {
        const [disPrice, setDisPrice] = useState(0);
        const [origPrice, setOrigPrice] = useState(0);
        const [addedQuantity, setAddedQuantity] = useState(0);
        const [showProductInfoModal, setShowProductInfoModal] = useState(false);
        const [selectedVariants, setSelectedVariants] = useState<Array<VariantsObj>>(
            new Array(product.quantity)
                ?.fill(product?.variants?.find((vv) => vv.availableForSale == true) || product?.variants?.[0])
                ?.map((variant) => ({
                    ...variant,
                    selling_plan: !product.oneTimePurchase
                        ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                        : "",
                    copyPrice: variant?.price,
                })),
        );
        const {
            isMobile,
            bundleConfig,
            standAlone,
            placeholderMap,
            money_format,
            currency,
            steps,
            activeStep,
            selectedVariants: globalSelectedVariants,
        } = useContext(PlaceholderBundleContext);

        const targetShops = ["dev-store-0124.myshopify.com", NAGAIA];

        const effectiveStepIndex = isAccorianTemplate ? (stepIndex ?? activeStep) : activeStep;

        const isMaxQuantityReached = useCallback(() => {
            const targetSubStepIdx = product.subStepIdx !== undefined ? product.subStepIdx : activeSubStep;
            const currentProduct = placeholderMap[effectiveStepIndex]?.shells?.find(
                (sp: ProductObj) =>
                    sp.id === product.id && (currentStep?.enable_subSteps ? sp.subStepIdx === targetSubStepIdx : true),
            );
            if (!currentProduct) return false;
            let count = currentProduct.selectedVariants?.length || 0;

            return bundleConfig?.limit_max_quantity_per_product
                ? count >= +bundleConfig?.max_quantity_per_product
                : false;
        }, [placeholderMap, effectiveStepIndex, product, activeSubStep, currentStep]);

        useEffect(() => {
            if (product?.id && placeholderMap[effectiveStepIndex]?.shells?.length > 0) {
                if (globalSelectedVariants?.length) {
                    const targetSubStepIdx = product.subStepIdx !== undefined ? product.subStepIdx : activeSubStep;

                    setAddedQuantity(
                        globalSelectedVariants?.find(
                            (sv: any) =>
                                effectiveStepIndex == sv?.page &&
                                (currentStep?.enable_subSteps ? sv?.subStepIdx === targetSubStepIdx : true) &&
                                sv?.id == selectedVariants[0].id,
                        )?.qty || 0,
                    );
                } else {
                    setAddedQuantity(0);
                }
            } else {
                setAddedQuantity(0);
            }
        }, [product, selectedVariants, globalSelectedVariants, activeSubStep, product.subStepIdx, effectiveStepIndex]);

        useEffect(() => {
            if (selectedVariants?.length) {
                let disP = bundleConfig?.apply_discount_on_compare_price
                    ? +selectedVariants[0].compareAtPrice * product?.quantity
                    : +selectedVariants[0].price * product?.quantity;
                const origP = bundleConfig?.apply_discount_on_compare_price
                    ? +selectedVariants[0].compareAtPrice * product?.quantity
                    : +selectedVariants[0].price * product?.quantity;
                const dis = bundleConfig?.discount_value || 0;
                switch (bundleConfig?.discount_type) {
                    case "percentage":
                        disP = disP - disP * (dis / 100);
                        break;
                    default:
                }
                setDisPrice(Math.round(disP * 100) / 100);
                setOrigPrice(Math.round(origP * 100) / 100);
            } else {
                setDisPrice(0);
                setOrigPrice(0);
            }
        }, [
            product,
            bundleConfig?.discount_type,
            bundleConfig?.discount_value,
            selectedVariants,
            bundleConfig?.apply_discount_on_compare_price,
        ]);

        useEffect(() => {
            if (product.checked) {
                // Add productIdx to each selectedVariant
                const updatedavailableVariants = selectedVariants?.map((variant: any) => ({
                    ...variant,
                    productIdx: index, // Add the product index
                }));
                product.selectedVariants = updatedavailableVariants; // Assign updated variants to the product

                // Update trackVariant state
                setTrackVariant((prevVariants: any[]) => {
                    const updatedVariants = [...prevVariants];
                    updatedVariants[index] = updatedavailableVariants; // Update the specific index with updated variants
                    return updatedVariants;
                });

                let totalOriginalPrice = 0;
                let totalDiscountedPrice = 0;

                // Calculate total original price
                updatedavailableVariants.forEach((variant: any) => {
                    if (product.checked) {
                        const variantPrice = +variant?.price || 0;
                        totalOriginalPrice += variantPrice;
                    }
                });
                // Apply discount based on bundleConfig after summing the prices
                totalDiscountedPrice = totalOriginalPrice;
                const discountValue = bundleConfig?.discount_value || 0;

                switch (bundleConfig?.discount_type) {
                    case "percentage":
                        totalDiscountedPrice = totalOriginalPrice - totalOriginalPrice * (discountValue / 100);
                        break;
                    default:
                        break;
                }

                // Set the final calculated prices, rounded to 2 decimal places
                setDisPrice(Math.round(totalDiscountedPrice * 100) / 100);
                setOrigPrice(Math.round(totalOriginalPrice * 100) / 100);
            }
        }, [selectedVariants, product, bundleConfig, index]);

        const handleProductClick = (e: any) => {
            e?.preventDefault();
            if (standAlone && !window.location.pathname.includes(product.handle)) {
                window.open(`https://${window?.location?.host}/products/${product.handle}`, "_blank");
            } else if (!window.location.pathname.includes(product.handle)) {
                window.open(`https://${shopify?.config?.shop}/products/${product.handle}`, "_blank");
            }
        };

        useEffect(() => {
            if (product.subscriptions && product.subscriptions.length > 0 && !product.oneTimePurchase) {
                product.isSubscribed = true;
            }
        }, [product, product.oneTimePurchase]);

        const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
        const effectiveMetafield = buildMetafieldFromSwatchOptions({
            product,
            bundleConfig,
        });
        const isProductAvailable = useMemo(() => {
            return product?.variants?.some(
                (vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale,
            );
        }, [product]);

        const renderVariantSelector = useMemo(
            () => (
                <>
                    {selectedVariants.map((_: any, i: number) =>
                        userShopname === "typsy-beauty.myshopify.com" ? (
                            <Swatches
                                bundlesDesign={bundleConfig?.bundles_design}
                                key={i}
                                variants={product.variants}
                                handleChange={setSelectedVariants}
                                selected={selectedVariants}
                                options={product.options || []}
                                productCardIndex={i}
                                standAlone={standAlone}
                            />
                        ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                            <CustomSwatches
                                key={i}
                                variants={product.variants}
                                handleChange={setSelectedVariants}
                                selected={selectedVariants}
                                options={product.options || []}
                                productCardIndex={i}
                                standAlone={standAlone}
                                metafield={effectiveMetafield}
                                bundlesDesign={bundleConfig?.bundles_design}
                                bundleConfig={bundleConfig}
                            />
                        ) : (
                            <VariantSelectorV2
                                key={i}
                                variants={product.variants}
                                handleChange={setSelectedVariants}
                                selected={selectedVariants}
                                options={product.options || []}
                                productCardIndex={i}
                                standAlone={standAlone}
                                metafield={effectiveMetafield}
                                bundlesDesign={bundleConfig?.bundles_design}
                                bundleConfig={bundleConfig}
                            />
                        ),
                    )}
                </>
            ),
            [
                selectedVariants,
                product.variants,
                product.options,
                bundleConfig?.show_swatches,
                bundleConfig?.bundles_design,
                effectiveMetafield,
            ],
        );

        const disableQuantitySelectorForPreselectedProducts = useCallback(() => {
            if (!bundleConfig?.make_pre_selected_compulsory) {
                return false;
            }

            const currentSubStepIdx = currentStep?.enable_subSteps ? activeSubStep : undefined;
            const preSelected = bundleConfig?.pre_selected_products;
            const isCurProductInPreSelected = preSelected.find((p: any) => p.id == product.id);
            if (!isCurProductInPreSelected) {
                return false;
            }

            const isCurProductInFinalList = placeholderMap
                .map((p: PlaceholderMap) => [...p.shells])
                .flat()
                .find(
                    (p: any) =>
                        p.id == product.id &&
                        effectiveStepIndex == p.page &&
                        (currentSubStepIdx !== undefined ? p.subStepIdx === currentSubStepIdx : true),
                );

            if (!isCurProductInFinalList) {
                return false;
            }

            const curProductTotalSelectedVariantsLength = isCurProductInFinalList?.selectedVariants?.length;
            const preselectedQty = isCurProductInPreSelected?.quantity || 1;

            if (curProductTotalSelectedVariantsLength <= preselectedQty) {
                return true;
            }

            return false;
        }, [placeholderMap, effectiveStepIndex, product]);

        const renderQuantitySelector = useMemo(
            () => (
                <>
                    {addedQuantity > 0 ? (
                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="quantitySelector">
                            <div className={styles.handleQtyWrapper}>
                                <button
                                    className={styles.bottomCta}
                                    style={{
                                        maxHeight: 40,
                                        minWidth: 10,
                                        paddingInline: 10,
                                        fontSize: 20,
                                        fontWeight: 650,
                                        border: `1px solid ${bundleConfig?.bundles_design?.add_to_cart_bg_color}`,
                                        background: bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                    }}
                                    onClick={() => {
                                        handleAddProduct(
                                            effectiveStepIndex,
                                            {
                                                ...product,
                                                selectedVariants,
                                                freeGift: isFreeGift,
                                                render_as_variants: currentStep?.render_as_variants,
                                            },
                                            "dec",
                                            currentStep?.enable_subSteps ? activeSubStep : undefined,
                                        );
                                    }}
                                    disabled={disableQuantitySelectorForPreselectedProducts()}
                                >
                                    -
                                </button>
                                <span
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        flexGrow: 1,
                                        minWidth: "40%",
                                        color: "#212121",
                                        fontSize: bundleConfig?.bundles_design.add_to_cart_text_size,
                                    }}
                                >
                                    {addedQuantity}
                                </span>
                                <button
                                    className={styles.bottomCta}
                                    style={{
                                        maxHeight: 40,
                                        minWidth: 10,
                                        paddingInline: 10,
                                        fontSize: 16,
                                        fontWeight: 650,
                                        border: `1px solid ${bundleConfig?.bundles_design?.add_to_cart_bg_color}`,
                                        background: bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                    }}
                                    disabled={disableCtas || isMaxQuantityReached()}
                                    onClick={() => {
                                        handleAddProduct(
                                            effectiveStepIndex,
                                            {
                                                ...product,
                                                selectedVariants,
                                                freeGift: isFreeGift,
                                                render_as_variants: currentStep?.render_as_variants,
                                            },
                                            "inc",
                                            currentStep?.enable_subSteps ? activeSubStep : undefined,
                                        );
                                    }}
                                >
                                    +
                                </button>
                            </div>
                        </InspectorEditableBlock>
                    ) : (
                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="buyNowCta">
                            <button
                                className={styles.bottomCta}
                                style={{
                                    minHeight: 40,
                                    maxHeight: "fit-content",
                                    marginTop: "auto",
                                    border: `1px solid ${bundleConfig?.bundles_design?.add_to_cart_bg_color}`,
                                    background: bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                    color: bundleConfig?.bundles_design.add_to_cart_text_color,
                                    fontSize: bundleConfig?.bundles_design.add_to_cart_text_size,
                                    fontWeight: bundleConfig?.bundles_design.add_to_cart_text_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design.add_to_cart_text_style?.split(" ")[1],
                                    wordBreak: "break-word",
                                }}
                                disabled={disableCtas || !isProductAvailable || isMaxQuantityReached()}
                                onClick={() => {
                                    handleAddProduct(
                                        effectiveStepIndex,
                                        {
                                            ...product,
                                            selectedVariants,
                                            freeGift: isFreeGift,
                                            render_as_variants: currentStep?.render_as_variants,
                                        },
                                        "add",
                                        currentStep?.enable_subSteps ? activeSubStep : undefined,
                                    );
                                }}
                            >
                                {isProductAvailable
                                    ? bundleConfig?.buy_now_cta_text
                                    : bundleConfig?.out_of_stock_text || "Out of stock!"}
                            </button>
                        </InspectorEditableBlock>
                    )}
                </>
            ),
            [
                addedQuantity,
                bundleConfig,
                disableCtas,
                isProductAvailable,
                effectiveStepIndex,
                activeSubStep,
                currentStep,
                selectedVariants,
                product,
                isFreeGift,
                handleAddProduct,
                isMaxQuantityReached,
            ],
        );

        const renderPriceArea = useMemo(
            () => (
                <>
                    {isProductAvailable &&
                        (isFreeGift ? (
                            <span
                                style={{
                                    color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                    fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                                    fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                }}
                            >
                                Free
                            </span>
                        ) : (
                            <RenderPrice
                                product={product}
                                bundleConfig={bundleConfig}
                                money_format={money_format}
                                currency={currency}
                                disPrice={addedQuantity > 0 ? disPrice : origPrice}
                                selectedVariants={selectedVariants}
                                origPrice={origPrice}
                                isMobile={isMobile}
                            />
                        ))}
                </>
            ),
            [
                isProductAvailable,
                isFreeGift,
                bundleConfig?.bundles_design,
                product,
                addedQuantity,
                disPrice,
                origPrice,
                selectedVariants,
                isMobile,
            ],
        );

        return (
            <InspectorEditableBlock
                bundleType={bundleConfig?.bundle_type}
                settingKey="productCard"
                tagsKey={product.tags?.toString()}
                productId={getProductId(product.id)}
            >
                <div
                    className={styles.productWrapper}
                    style={{
                        border:
                            addedQuantity > 0 && isAccorianTemplate
                                ? `1.5px solid #1a1a1a`
                                : `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                        borderRadius: bundleConfig?.bundles_design?.border_radius,
                        backgroundColor: bundleConfig?.bundles_design.product_background_color,
                        minWidth: isMobile
                            ? getPlaceholderCardWidth(bundleConfig?.product_card_mobile_size || "medium", "mobile")
                            : getPlaceholderCardWidth(bundleConfig?.product_card_desktop_size || "medium", "desktop"),
                        maxWidth: isMobile
                            ? getPlaceholderCardWidth(bundleConfig?.product_card_mobile_size || "medium", "mobile")
                            : getPlaceholderCardWidth(bundleConfig?.product_card_desktop_size || "medium", "desktop"),
                        ...(isAccorianTemplate && { height: "100%" }),
                    }}
                >
                    <div className={styles.productInfoRight}>
                        {addedQuantity > 0 && isAccorianTemplate && (
                            <div className={styles.tickMark}>
                                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                                    <path
                                        d="M2.5 7L5.5 10L11.5 4"
                                        stroke="white"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                        )}
                        <ProductImageWrapper
                            product={product}
                            handleProductClick={() => {
                                setShowProductInfoModal(true);
                            }}
                            bundleConfig={{
                                ...bundleConfig,
                                bundles_design: {
                                    ...bundleConfig?.bundles_design,
                                    product_img_size: bundleConfig?.bundles_design.product_img_size
                                        ? "bb_" + bundleConfig?.bundles_design.product_img_size
                                        : "bb_small",
                                },
                            }}
                            overrideHeight={
                                isMobile
                                    ? bundleConfig?.bundles_design?.product_custom_img_size_mobile
                                    : bundleConfig?.bundles_design?.product_custom_img_size
                            }
                            isMobile={isMobile}
                            selectedVariants={selectedVariants}
                            showHoverIcon={true}
                        />

                        <Suspense fallback={null}>
                            {showProductInfoModal && (
                                <ProductInfoModal
                                    product={product}
                                    showProductInfoModal={showProductInfoModal}
                                    onClose={() => setShowProductInfoModal(false)}
                                    renderVariantSelector={renderVariantSelector}
                                    renderQuantitySelector={renderQuantitySelector}
                                    renderPriceArea={
                                        targetShops?.includes(userShopname as string) ? <></> : renderPriceArea
                                    }
                                    selectedVariants={selectedVariants}
                                />
                            )}
                        </Suspense>

                        <div
                            className={styles.productMetaRight}
                            style={{
                                color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                                fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                gap: isMobile
                                    ? +getPixelsForImage(bundleConfig?.bundles_design?.product_img_size, "number") /
                                          10 +
                                      "px"
                                    : "0px",
                                whiteSpace: "normal" /* Allows text to wrap onto the next line */,
                                ...(isAccorianTemplate && { flex: 1 }),
                            }}
                        >
                            <div
                                style={{
                                    fontSize: bundleConfig?.bundles_design.product_name_size,
                                    color: bundleConfig?.bundles_design?.product_name_color,
                                    fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                    cursor: "pointer",
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "stretch",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    height: "100%",
                                }}
                                onClick={handleProductClick}
                            >
                                <span
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        justifyContent: "flex-start",
                                        gap: 4,
                                    }}
                                >
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="productTitle"
                                    >
                                        <div
                                            style={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                alignItems: isMobile ? "center" : "flex-start",
                                                justifyContent: isMobile ? "space-between" : "flex-start",
                                                gap: isMobile ? 4 : 8,
                                                lineHeight: "normal",
                                            }}
                                            title={
                                                steps[effectiveStepIndex]?.render_as_variants
                                                    ? product.parentName
                                                    : product?.title
                                            }
                                        >
                                            {steps[effectiveStepIndex]?.render_as_variants
                                                ? product.parentName
                                                : product?.title}
                                        </div>
                                    </InspectorEditableBlock>
                                    {targetShops?.includes(userShopname as string) && !!selectedVariants[0]?.sku && (
                                        <div
                                            style={{
                                                fontSize: (bundleConfig?.bundles_design.product_name_size || 14) - 2,
                                                color: bundleConfig?.bundles_design?.product_name_color,
                                                opacity: 0.7,
                                                lineHeight: "normal",
                                            }}
                                        >
                                            {selectedVariants[0]?.sku}
                                        </div>
                                    )}

                                    {targetShops?.includes(userShopname as string) ? (
                                        <></>
                                    ) : bundleConfig?.hide_prices_product_card &&
                                      bundleConfig?.discount_type == DISCOUNT_TYPES.FIXED ? (
                                        <></>
                                    ) : (
                                        renderPriceArea
                                    )}
                                    {!!steps[effectiveStepIndex]?.render_as_variants && (
                                        <RenderVariantInfo product={product} />
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>

                    {product.variants.length > 1 && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "5px",
                                width: "100%",
                            }}
                        >
                            {renderVariantSelector}
                        </div>
                    )}

                    {renderQuantitySelector}
                </div>
            </InspectorEditableBlock>
        );
    },
);

export default React.memo(ProductCard);
